import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InboxOutlined,
  DownloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import { MONTHS_OPTIONS } from '@/shared/constants';
import FindingsList from './FindingsList';

const { Dragger } = Upload;

/**
 * @param {number} value - Amount
 * @returns {string} Amount with thousands separators
 */
const money = (value) =>
  Number(value || 0).toLocaleString('sr-RS', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Accountants type the rate with a decimal comma ("117,3531"); InputNumber
 * would drop the comma and read 1173531. Either separator is the decimal one,
 * since a rate never carries thousands separators.
 * @param {string} text - What was typed
 * @returns {string} The same number with a decimal point
 */
const parseRate = (text) => String(text ?? '').replace(/\s/g, '').replace(',', '.');

/**
 * AB Soft journal exports keep their report name in the file name
 * ("5 Dnevnik knjiženja…", "BP - 5 - dodatne karakteristike…", "BR UG…").
 * Dropped onto the template field they would replace the real template.
 * @param {string} name - File name
 * @returns {boolean} Whether the file is a journal export rather than a template
 */
const looksLikeJournal = (name = '') =>
  /dnevnik|karakteristik|^\s*(BP|BR)\b/i.test(name);

/**
 * @param {string|Date} value - Timestamp
 * @returns {string} dd.mm.yyyy hh:mm in local time
 */
const dateTime = (value) =>
  new Date(value).toLocaleString('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Upload the exports, see exactly what they translate into, and only then produce
 * the group's file.
 *
 * The check is deliberately a separate step: the same translation runs either
 * way, so whatever is approved on screen is what the downloaded workbook holds.
 *
 * @param {Object} props - Component props
 * @param {Array} props.packages - Reporting packages to choose from
 * @returns {JSX.Element} The run wizard
 */
export default function RunWizardPanel({ packages = [] }) {
  const { t } = useTranslation(['tables', 'messages', 'forms']);
  const gr = (key) => t(`groupReporting.${key}`, { ns: 'tables' });

  const [form] = Form.useForm();
  const [journals, setJournals] = useState([]);
  // 'ledger' = the journals the AB machine sends every night; 'upload' = the
  // four exports attached by hand, as before.
  const [journalSource, setJournalSource] = useState('ledger');
  const fromLedger = journalSource === 'ledger';
  const hasJournals = fromLedger || journals.length > 0;
  const [template, setTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const packageId = Form.useWatch('packageId', form);
  const year = Form.useWatch('year', form);

  const { data: ledgerStatus } = useQuery({
    queryKey: ['groupReportingLedgerStatus', packageId, year],
    queryFn: () => API.groupReporting.getPackageLedgerStatus(packageId, year),
    enabled: fromLedger && Boolean(packageId) && Boolean(year),
    select: (response) => response.data,
  });

  // Month names come from the shared list every other screen uses, so they read
  // in the viewer's language. The template's own English month labels are the
  // writer's business, not the picker's.
  const monthOptions = useMemo(
    () =>
      MONTHS_OPTIONS.map((month) => ({
        value: month.value,
        label: t(month.key, { ns: 'forms' }),
      })),
    [t]
  );

  const packageOptions = useMemo(
    () =>
      packages.map((item) => ({
        value: item._id,
        label: `${item.code} — ${item.name}`,
      })),
    [packages]
  );

  /**
   * @returns {Object} Values shared by the check and the generate call
   */
  const readForm = async () => {
    const values = await form.validateFields();
    return {
      packageId: values.packageId,
      year: values.year,
      month: values.month,
      fxRate: values.fxRate,
      fromLedger,
      journals: fromLedger ? [] : journals.map((file) => file.originFileObj || file),
    };
  };

  const previewMutation = useMutation({
    mutationFn: async () => API.groupReporting.previewRun(await readForm()),
    onSuccess: (response) => {
      setPreview(response.data);
      message.success(gr('previewSuccess'));
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || error.message);
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const payload = await readForm();
      return API.groupReporting.generateRun({
        ...payload,
        template: template?.originFileObj || template,
      });
    },
    onSuccess: (result) => {
      // The response is the workbook itself, so the download is started here.
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success(gr('generateSuccess'));
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || error.message);
    },
  });

  const hasBlockingFinding = (preview?.findings || []).some(
    (finding) => finding.severity === 'ERROR'
  );

  const summaryColumns = [
    { title: gr('block'), dataIndex: 'block', key: 'block' },
    { title: gr('unit'), dataIndex: 'unit', key: 'unit', width: 90 },
    {
      title: gr('code'),
      dataIndex: 'code',
      key: 'code',
      render: (code, row) => (
        <Space size={6}>
          <span>{code}</span>
          {row.viaFallback && <Tag color="orange">{gr('viaFallback')}</Tag>}
        </Space>
      ),
    },
    {
      title: gr('amountSource'),
      dataIndex: 'amountSource',
      key: 'amountSource',
      align: 'right',
      render: money,
    },
    {
      title: gr('amountTarget'),
      dataIndex: 'amountTarget',
      key: 'amountTarget',
      align: 'right',
      render: (value) => <strong>{money(value)}</strong>,
    },
    {
      title: gr('rows'),
      dataIndex: 'rowCount',
      key: 'rowCount',
      align: 'right',
      width: 90,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ year: new Date().getFullYear() }}
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="packageId"
                label={gr('selectPackage')}
                rules={[{ required: true }]}
              >
                <Select
                  options={packageOptions}
                  placeholder={gr('selectPackage')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name="year"
                label={gr('year')}
                rules={[{ required: true }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={2000}
                  max={2100}
                  controls={false}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item
                name="month"
                label={gr('month')}
                rules={[{ required: true }]}
              >
                <Select options={monthOptions} placeholder={gr('month')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item
                name="fxRate"
                label={gr('fxRate')}
                extra={gr('fxRateHint')}
                rules={[{ required: true }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.0001}
                  parser={parseRate}
                  controls={false}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={gr('journalSource')}>
            <Segmented
              value={journalSource}
              onChange={(value) => {
                setJournalSource(value);
                setPreview(null);
              }}
              options={[
                { value: 'ledger', label: gr('journalSourceLedger') },
                { value: 'upload', label: gr('journalSourceUpload') },
              ]}
            />
            {fromLedger && (
              <Alert
                style={{ marginTop: 10 }}
                type="info"
                showIcon
                message={gr('journalSourceLedgerTitle')}
                description={
                  <>
                    <div>{gr('journalSourceLedgerHint')}</div>
                    {packageId && ledgerStatus && (
                      <div style={{ marginTop: 4 }}>
                        <strong>
                          {ledgerStatus.receivedAt
                            ? `${gr('ledgerReceivedAt')}: ${dateTime(ledgerStatus.receivedAt)}`
                            : gr('ledgerNotReceived')}
                        </strong>
                      </div>
                    )}
                  </>
                }
              />
            )}
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={14} style={{ display: fromLedger ? 'none' : undefined }}>
              <Form.Item label={gr('journalFiles')} required>
                <Dragger
                  multiple
                  accept=".xlsx,.xls"
                  beforeUpload={() => false}
                  fileList={journals}
                  onChange={({ fileList }) => setJournals(fileList)}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">{gr('dropJournals')}</p>
                </Dragger>
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item label={gr('templateFile')}>
                <Dragger
                  maxCount={1}
                  accept=".xlsx,.xls"
                  beforeUpload={(file) => {
                    if (looksLikeJournal(file.name)) {
                      message.warning({
                        content: `${file.name}: ${gr(
                          fromLedger ? 'journalNotNeeded' : 'journalInTemplate'
                        )}`,
                        duration: 8,
                      });
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                  fileList={template ? [template] : []}
                  onChange={({ fileList }) =>
                    setTemplate(fileList[fileList.length - 1] || null)
                  }
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">{gr('dropTemplate')}</p>
                </Dragger>
              </Form.Item>
            </Col>
          </Row>

          <Space wrap>
            <Button
              type="default"
              className="!rounded-[var(--buttonRadius)]"
              icon={<SafetyCertificateOutlined />}
              loading={previewMutation.isPending}
              disabled={!hasJournals}
              onClick={() => previewMutation.mutate()}
            >
              {gr('preview')}
            </Button>
            <Button
              type="primary"
              className="!rounded-[var(--buttonRadius)]"
              icon={<DownloadOutlined />}
              loading={generateMutation.isPending}
              disabled={
                !hasJournals || !template || !preview || hasBlockingFinding
              }
              onClick={() => generateMutation.mutate()}
            >
              {gr('generate')}
            </Button>
            {!preview && (
              <span style={{ opacity: 0.65 }}>{gr('previewFirst')}</span>
            )}
          </Space>
        </Form>
      </Card>

      {preview && (
        <>
          <Card size="small" title={gr('sourceFiles')}>
            <Space wrap size={8}>
              {preview.sourceFiles.map((file) => (
                <Tag key={file.fileName} color="blue">
                  {file.fileName} · {gr('sourceClass')} {file.sourceClass} ·{' '}
                  {file.rowCount} {gr('rows').toLowerCase()} ·{' '}
                  {money(file.sourceTotal)}
                </Tag>
              ))}
            </Space>
          </Card>

          <Card size="small" title={gr('findings')}>
            <FindingsList findings={preview.findings} />
            {hasBlockingFinding && (
              <>
                <Divider />
                <Alert
                  type="error"
                  showIcon
                  message={gr('checkBeforeGenerating')}
                />
              </>
            )}
          </Card>

          <Card size="small" title={gr('summary')}>
            <Table
              size="small"
              rowKey={(row) => `${row.block}-${row.unit}-${row.code}`}
              columns={summaryColumns}
              dataSource={preview.summary}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              scroll={{ x: 'max-content' }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>{gr('total')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{money(preview.totals.sourceAmount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{money(preview.totals.targetAmount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              )}
            />
          </Card>
        </>
      )}
    </Space>
  );
}
